import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  call,
  confirmCompanySpreadsheetUpload,
  confirmGroupSpreadsheetUpload,
  downloadSpreadsheetTemplate,
  uploadCompanySpreadsheet,
  uploadGroupSpreadsheet,
  UploadStatus,
  type GroupSpreadsheetUploadAbsenceResponse,
  type GroupSpreadsheetUploadCompanyCounts,
  type GroupSpreadsheetUploadContractCounts,
  type GroupSpreadsheetUploadPreviewResponse,
  type GroupSpreadsheetUploadRowResponse,
  type SpreadsheetUploadAbsenceResponse,
  type SpreadsheetUploadContractCounts,
  type SpreadsheetUploadPreviewResponse,
  type SpreadsheetUploadRowResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  FileField,
  Form,
  FormError,
  PageHeader,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatCpf, formatCurrency, formatDate, formatInteger } from '../../format';
import { useRequiredSession } from '../../session';
import { saveAsAttachment } from '../../utils/download';
import { usePortalContracts } from '../queries';
import { useCompanyScope } from './companyScope';
import {
  ACTION_LABELS,
  OUTCOME_LABELS,
  UPLOAD_COPY,
  companyLabel,
  contractLabel,
  fileRefusalMessage,
  isRejected,
  messageForRejection,
  rejectedRowsCsv,
  rejectedRowsFilename,
  type UploadTarget,
} from './spreadsheetUpload';

/**
 * Sending in the movement spreadsheet, in two steps.
 *
 * The screen is the API's own shape and deliberately adds nothing to it: the
 * first call returns a verdict for every line and creates nothing, the second
 * commits the ready ones. What the screen contributes is the pause between
 * them -- the preview is on the page, with the rejected rows readable and
 * exportable, before the button that commits anything appears.
 *
 * That is the point of the whole feature. A customer sends four hundred rows,
 * two of them are wrong, and the answer has to be "these two, and here is why"
 * rather than "the file was rejected".
 *
 * The sheet is the company's, not one contract's: it may list people of every
 * sub-contract, and the server routes each row to the contract its
 * Subestipulante and Módulo select. So every row says which contract it went
 * to, and the counts are shown per contract as well as for the whole file.
 *
 * And because the sheet lists everybody, it is also read the other way round:
 * the people the roster has and the sheet does not mention are listed below the
 * rows, with the one thing a customer has to know about them -- nothing happens
 * to them unless an exclusão is sent.
 *
 * A person registered at an economic group (#391) usually has one sheet for all
 * of the group's companies, so they choose what the sheet is for: the whole
 * group, which is the default, or the company selected in the switcher, which is
 * the company flow above unchanged. A group sheet is previewed and confirmed
 * through the group's routes, and every row, absence and count names its
 * company beside its contract. Anybody else sees no choice and no difference.
 */

/** U+FEFF. Written as an escape because an invisible character in source is a
 *  character nobody can see they deleted. */
const BYTE_ORDER_MARK = '\uFEFF';

interface Summary {
  readonly label: string;
  readonly value: number;
}

/** What is on screen: one company's preview, or one for the whole group. */
type Preview =
  | { readonly target: 'company'; readonly data: SpreadsheetUploadPreviewResponse }
  | { readonly target: 'group'; readonly data: GroupSpreadsheetUploadPreviewResponse };

function summaryOf(preview: Preview['data']): readonly Summary[] {
  const { upload } = preview;
  return [
    { label: 'Linhas na planilha', value: upload.row_count },
    { label: 'Prontas', value: upload.ready_count },
    { label: 'Enviadas', value: upload.committed_count },
    { label: 'Sem movimentação', value: upload.ignored_count },
    { label: 'Recusadas', value: upload.rejected_count },
    { label: 'Seguradas fora da planilha', value: upload.absent_count ?? 0 },
  ];
}

const ABSENT_COLUMNS: readonly Column<SpreadsheetUploadAbsenceResponse>[] = [
  { key: 'contract', header: 'Contrato', render: (absence) => contractLabel(absence.contract) },
  { key: 'cpf', header: 'CPF', render: (absence) => <code>{formatCpf(absence.cpf)}</code> },
  { key: 'full_name', header: 'Nome', render: (absence) => absence.full_name },
];

const GROUP_ABSENT_COLUMNS: readonly Column<GroupSpreadsheetUploadAbsenceResponse>[] = [
  { key: 'company', header: 'Empresa', render: (absence) => companyLabel(absence.company) },
  ...ABSENT_COLUMNS,
];

const COUNT_COLUMNS: readonly Column<
  SpreadsheetUploadContractCounts | GroupSpreadsheetUploadCompanyCounts
>[] = [
  {
    key: 'ready',
    header: 'Prontas',
    align: 'end',
    render: (entry) => formatInteger(entry.ready_count),
  },
  {
    key: 'committed',
    header: 'Enviadas',
    align: 'end',
    render: (entry) => formatInteger(entry.committed_count),
  },
  {
    key: 'ignored',
    header: 'Sem movimentação',
    align: 'end',
    render: (entry) => formatInteger(entry.ignored_count),
  },
  {
    key: 'rejected',
    header: 'Recusadas',
    align: 'end',
    render: (entry) => formatInteger(entry.rejected_count),
  },
];

const COMPANY_COLUMNS: readonly Column<GroupSpreadsheetUploadCompanyCounts>[] = [
  { key: 'company', header: 'Empresa', render: (entry) => companyLabel(entry.company) },
  ...COUNT_COLUMNS,
  {
    key: 'absent',
    header: 'Seguradas fora da planilha',
    align: 'end',
    render: (entry) => formatInteger(entry.absent_count ?? 0),
  },
];

const CONTRACT_COLUMNS: readonly Column<SpreadsheetUploadContractCounts>[] = [
  { key: 'contract', header: 'Contrato', render: (entry) => contractLabel(entry.contract) },
  ...COUNT_COLUMNS,
];

const GROUP_CONTRACT_COLUMNS: readonly Column<GroupSpreadsheetUploadContractCounts>[] = [
  { key: 'company', header: 'Empresa', render: (entry) => companyLabel(entry.company) },
  { key: 'contract', header: 'Contrato', render: (entry) => contractLabel(entry.contract) },
  ...COUNT_COLUMNS,
];

const ROW_DETAIL_COLUMNS: readonly Column<SpreadsheetUploadRowResponse>[] = [
  {
    key: 'outcome',
    header: 'Situação',
    render: (row) => OUTCOME_LABELS[row.outcome] ?? row.outcome,
  },
  {
    key: 'action',
    header: 'Movimentação',
    render: (row) =>
      row.action === null || row.action === undefined ? '—' : (ACTION_LABELS[row.action] ?? '—'),
  },
  {
    key: 'cpf',
    header: 'CPF',
    render: (row) =>
      row.cpf === null || row.cpf === undefined ? '—' : <code>{formatCpf(row.cpf)}</code>,
  },
  { key: 'full_name', header: 'Nome', render: (row) => row.full_name ?? '—' },
  {
    key: 'effective_date',
    header: 'Vigência',
    render: (row) =>
      row.effective_date === null || row.effective_date === undefined
        ? '—'
        : formatDate(row.effective_date),
  },
  {
    key: 'salary',
    header: 'Salário',
    align: 'end',
    render: (row) =>
      row.salary === null || row.salary === undefined ? '—' : formatCurrency(row.salary),
  },
];

function rowColumns(target: UploadTarget): readonly Column<SpreadsheetUploadRowResponse>[] {
  return [
    { key: 'row_number', header: 'Linha', align: 'end', render: (row) => row.row_number },
    { key: 'contract', header: 'Contrato', render: (row) => contractLabel(row.contract) },
    ...ROW_DETAIL_COLUMNS,
    {
      key: 'reason',
      header: 'Motivo',
      render: (row) => messageForRejection(row, target) || '—',
    },
  ];
}

const GROUP_COLUMNS: readonly Column<GroupSpreadsheetUploadRowResponse>[] = [
  { key: 'row_number', header: 'Linha', align: 'end', render: (row) => row.row_number },
  { key: 'company', header: 'Empresa', render: (row) => companyLabel(row.company) },
  ...rowColumns('group').slice(1),
];

const COLUMNS = rowColumns('company');

export function SpreadsheetUploadPage() {
  const { company } = useCompanyScope();
  const group = useRequiredSession().principal.group ?? null;
  const contracts = usePortalContracts(company.id);
  const { notify } = useNotifications();

  const [target, setTarget] = useState<UploadTarget>(group !== null ? 'group' : 'company');
  const [file, setFile] = useState<File | null>(null);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [onlyRejected, setOnlyRejected] = useState(false);

  const submit = useMutation({
    mutationFn: async (): Promise<Preview> => {
      if (file === null) {
        throw new Error('no file');
      }
      const body = { file, ...(effectiveDate !== '' && { effective_date: effectiveDate }) };
      if (target === 'group' && group !== null) {
        const data = await call(uploadGroupSpreadsheet({ path: { group_id: group.id }, body }));
        return { target: 'group', data };
      }
      const data = await call(uploadCompanySpreadsheet({ path: { company_id: company.id }, body }));
      return { target: 'company', data };
    },
    onMutate: () => {
      // Whatever happens to this file, the preview on screen is no longer the
      // answer to it. Left up, an earlier sheet's rows would sit under this
      // sheet's refusal looking like its result -- with a confirm button.
      setPreview(null);
      commit.reset();
    },
    onSuccess: (result) => {
      setPreview(result);
      setOnlyRejected(false);
      if (result.data.is_duplicate === true) {
        notify(
          'warning',
          result.target === 'group' ? UPLOAD_COPY.groupDuplicate : UPLOAD_COPY.duplicate,
        );
      }
    },
  });

  const commit = useMutation({
    mutationFn: async (): Promise<Preview> => {
      if (preview === null) {
        throw new Error('no preview');
      }
      if (preview.target === 'group') {
        const data = await call(
          confirmGroupSpreadsheetUpload({
            path: { group_id: preview.data.upload.group_id, upload_id: preview.data.upload.id },
          }),
        );
        return { target: 'group', data };
      }
      const data = await call(
        confirmCompanySpreadsheetUpload({
          path: { company_id: company.id, upload_id: preview.data.upload.id },
        }),
      );
      return { target: 'company', data };
    },
    onSuccess: (result) => {
      setPreview(result);
      notify('success', UPLOAD_COPY.committed);
    },
  });

  const chooseTarget = (next: UploadTarget) => {
    if (next === target) {
      return;
    }
    // A preview is the answer for one target; it is not shown under the other.
    setTarget(next);
    setPreview(null);
    submit.reset();
    commit.reset();
  };

  const template = useMutation({
    mutationFn: (format: 'xlsx' | 'csv') =>
      call(downloadSpreadsheetTemplate({ query: { format } })),
    onSuccess: (blob, format) => {
      saveAsAttachment(blob as Blob, `modelo-vylife-movimentacoes.${format}`);
    },
  });

  const exportRejected = () => {
    if (preview === null) {
      return;
    }
    // The byte-order mark is what makes Excel on a Brazilian machine read this
    // as UTF-8 instead of showing "Salário" as "SalÃ¡rio".
    const blob = new Blob([BYTE_ORDER_MARK, rejectedRowsCsv(preview.data.rows, preview.target)], {
      type: 'text/csv;charset=utf-8',
    });
    saveAsAttachment(blob, rejectedRowsFilename());
  };

  const upload = preview?.data.upload;
  const isCommitted = upload?.status === UploadStatus.COMMITTED;
  const hasRejected = (upload?.rejected_count ?? 0) > 0;

  const form = (
    <Form onSubmit={() => submit.mutate()}>
      <FormError error={submit.error} message={fileRefusalMessage(submit.error)} />
      {group !== null && (
        <SelectField<UploadTarget>
          label={UPLOAD_COPY.targetLegend}
          value={target}
          onChange={(next) => chooseTarget(next === '' ? 'group' : next)}
          options={[
            { value: 'group', label: UPLOAD_COPY.targetGroup(group.name) },
            { value: 'company', label: UPLOAD_COPY.targetCompany(company.name) },
          ]}
          hint={target === 'group' ? UPLOAD_COPY.targetGroupHint : UPLOAD_COPY.targetCompanyHint}
        />
      )}
      <FileField
        label={UPLOAD_COPY.fileLabel}
        hint={UPLOAD_COPY.fileHint}
        accept=".xlsx,.csv"
        required
        onChange={setFile}
      />
      <TextField
        label={UPLOAD_COPY.effectiveDateLabel}
        hint={UPLOAD_COPY.effectiveDateHint}
        type="date"
        value={effectiveDate}
        onChange={setEffectiveDate}
      />
      <div className="form__actions">
        <SubmitButton pending={submit.isPending} pendingLabel="Enviando…">
          {UPLOAD_COPY.submit}
        </SubmitButton>
      </div>
    </Form>
  );

  return (
    <div className="page">
      <PageHeader title={UPLOAD_COPY.title} description={UPLOAD_COPY.description} />

      <Panel title={UPLOAD_COPY.templateTitle} description={UPLOAD_COPY.templateDescription}>
        <div className="form__actions">
          <button
            type="button"
            className="button button--secondary"
            onClick={() => template.mutate('xlsx')}
          >
            {UPLOAD_COPY.downloadXlsx}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => template.mutate('csv')}
          >
            {UPLOAD_COPY.downloadCsv}
          </button>
        </div>
      </Panel>

      <Panel title={UPLOAD_COPY.formTitle}>
        {target === 'group' ? (
          // The group's sheet does not depend on the selected company having a
          // contract of its own: its rows go to whichever company they name.
          form
        ) : (
          <AsyncSection
            state={contracts}
            loadingLabel="Carregando contratos…"
            errorTitle="Não foi possível carregar os contratos"
            isEmpty={(data) => data.items.length === 0}
            emptyTitle="Nenhum contrato"
            emptyDescription={UPLOAD_COPY.noContract}
          >
            {() => form}
          </AsyncSection>
        )}
      </Panel>

      {preview !== null && upload !== undefined && (
        <Panel
          title={UPLOAD_COPY.previewTitle}
          description={isCommitted ? UPLOAD_COPY.committed : UPLOAD_COPY.previewDescription}
        >
          <dl className="definitions">
            {summaryOf(preview.data).map((entry) => (
              <div key={entry.label}>
                <dt>{entry.label}</dt>
                <dd>{formatInteger(entry.value)}</dd>
              </div>
            ))}
          </dl>

          <Breakdown preview={preview} />

          {hasRejected && (
            <div className="form__actions">
              <label className="field__label" htmlFor="only-rejected">
                <input
                  id="only-rejected"
                  type="checkbox"
                  checked={onlyRejected}
                  onChange={(event) => setOnlyRejected(event.target.checked)}
                />{' '}
                {UPLOAD_COPY.onlyRejected}
              </label>
              <button type="button" className="button button--ghost" onClick={exportRejected}>
                {UPLOAD_COPY.exportRejected}
              </button>
            </div>
          )}

          <Rows preview={preview} onlyRejected={onlyRejected} />

          <Absences preview={preview} />

          <FormError error={commit.error} />

          {!isCommitted && (
            <div className="form__actions">
              <button
                type="button"
                className="button button--primary"
                disabled={commit.isPending || upload.ready_count === 0}
                onClick={() => commit.mutate()}
              >
                {commit.isPending ? UPLOAD_COPY.confirming : UPLOAD_COPY.confirm}
              </button>
              {upload.ready_count === 0 && (
                <p className="panel__footnote">{UPLOAD_COPY.nothingToCommit}</p>
              )}
            </div>
          )}

          {isCommitted && (
            <p className="panel__footnote">
              {formatInteger(upload.committed_count)} linha(s) enviada(s) como movimentações. As
              recusadas continuam aqui para você corrigir e reenviar.
            </p>
          )}
        </Panel>
      )}
    </div>
  );
}

/** The counts per company (a group sheet only) and per contract. */
function Breakdown({ preview }: { preview: Preview }) {
  if (preview.target === 'group') {
    const { company_counts: perCompany = [], contract_counts: perContract = [] } =
      preview.data.upload;
    return (
      <>
        {perCompany.length > 0 && (
          <>
            <h3 className="panel__subtitle">{UPLOAD_COPY.companiesTitle}</h3>
            <DataTable
              caption={UPLOAD_COPY.companiesCaption}
              columnVisibility={false}
              columns={COMPANY_COLUMNS}
              rows={perCompany}
              rowKey={(entry) => entry.company?.id ?? 'sem-empresa'}
              emptyTitle="Nenhuma linha para mostrar"
            />
          </>
        )}
        {perContract.length > 0 && (
          <>
            <h3 className="panel__subtitle">{UPLOAD_COPY.contractsTitle}</h3>
            <DataTable
              caption={UPLOAD_COPY.contractsCaption}
              columnVisibility={false}
              columns={GROUP_CONTRACT_COLUMNS}
              rows={perContract}
              rowKey={(entry) => entry.contract?.id ?? 'sem-contrato'}
              emptyTitle="Nenhuma linha para mostrar"
            />
          </>
        )}
      </>
    );
  }
  const perContract = preview.data.upload.contract_counts ?? [];
  if (perContract.length === 0) {
    return null;
  }
  return (
    <>
      <h3 className="panel__subtitle">{UPLOAD_COPY.contractsTitle}</h3>
      <DataTable
        caption={UPLOAD_COPY.contractsCaption}
        columnVisibility={false}
        columns={CONTRACT_COLUMNS}
        rows={perContract}
        rowKey={(entry) => entry.contract?.id ?? 'sem-contrato'}
        emptyTitle="Nenhuma linha para mostrar"
      />
    </>
  );
}

function Rows({ preview, onlyRejected }: { preview: Preview; onlyRejected: boolean }) {
  const common = {
    caption: 'Linhas da planilha enviada',
    rowKey: (row: SpreadsheetUploadRowResponse) => String(row.row_number),
    emptyTitle: 'Nenhuma linha para mostrar',
    columnVisibility: false,
  };
  if (preview.target === 'group') {
    const rows = onlyRejected ? preview.data.rows.filter(isRejected) : preview.data.rows;
    return <DataTable {...common} columns={GROUP_COLUMNS} rows={rows} />;
  }
  const rows = onlyRejected ? preview.data.rows.filter(isRejected) : preview.data.rows;
  return <DataTable {...common} columns={COLUMNS} rows={rows} />;
}

function Absences({ preview }: { preview: Preview }) {
  const absences = preview.data.absences ?? [];
  if (absences.length === 0) {
    return null;
  }
  return (
    <>
      <h3 className="panel__subtitle">{UPLOAD_COPY.absentTitle}</h3>
      <p className="panel__footnote">{UPLOAD_COPY.absentDescription}</p>
      {preview.target === 'group' ? (
        <DataTable
          caption={UPLOAD_COPY.absentCaption}
          columnVisibility={false}
          columns={GROUP_ABSENT_COLUMNS}
          rows={preview.data.absences ?? []}
          rowKey={(absence) => `${absence.company.id}:${absence.contract.id}:${absence.cpf}`}
          emptyTitle="Nenhuma pessoa para mostrar"
        />
      ) : (
        <DataTable
          caption={UPLOAD_COPY.absentCaption}
          columnVisibility={false}
          columns={ABSENT_COLUMNS}
          rows={preview.data.absences ?? []}
          rowKey={(absence) => `${absence.contract.id}:${absence.cpf}`}
          emptyTitle="Nenhuma pessoa para mostrar"
        />
      )}
    </>
  );
}
